import { useEffect, useState } from "react";
import { Eye, MousePointerClick, MessageCircle, Star, CalendarCheck } from "lucide-react";
import { api, cardCls } from "@/lib/api";

const ACTIVITY_LABELS = {
  appointment_request: "Appointment request received",
  appointment_confirmed: "Appointment confirmed",
  appointment_alternative: "Alternative time proposed",
  appointment_completed: "Appointment completed",
  appointment_cancelled: "Appointment cancelled",
  appointment_declined: "Appointment declined",
};

const timeAgo = (d) => {
  const mins = Math.floor((Date.now() - new Date(d)) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const ActivityTab = ({ goCalendar }) => {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api("/insights").then(setData).catch(() => setData({ page_visits: 0, service_views: 0, whatsapp_clicks: 0, top_service: null }));
    api("/activity/recent").then((d) => setItems(d.items)).catch(() => {});
  }, []);

  const cards = [
    { icon: Eye, value: data?.page_visits ?? 0, label: "Page visits", testid: "stat-page-visits" },
    { icon: MousePointerClick, value: data?.service_views ?? 0, label: "Service views", testid: "stat-service-views" },
    { icon: MessageCircle, value: data?.whatsapp_clicks ?? 0, label: "WhatsApp enquiry clicks", testid: "stat-whatsapp-clicks" },
  ];

  return (
    <div data-testid="activity-tab">
      <h1 className="font-serif text-3xl font-medium tracking-tight text-guinep-ink">Activity</h1>
      <p className="mt-1.5 text-sm text-guinep-mute">A simple look at the last 7 days.</p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {cards.map((c) => (
          <div key={c.label} className={`${cardCls} p-4`} data-testid={c.testid}>
            <c.icon size={16} className="text-guinep-mid" />
            <p className="mt-3 font-serif text-3xl font-medium text-guinep-ink">{c.value}</p>
            <p className="mt-0.5 text-xs text-guinep-mute">{c.label}</p>
          </div>
        ))}
        <div className={`${cardCls} p-4`} data-testid="stat-top-service">
          <Star size={16} className="text-guinep-mid" />
          <p className="mt-3 text-sm font-semibold text-guinep-ink">{data?.top_service || "No service views yet"}</p>
          <p className="mt-0.5 text-xs text-guinep-mute">Most viewed service</p>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-7" data-testid="activity-recent">
          <p className="text-sm font-semibold text-guinep-ink">Recent</p>
          <div className="mt-2.5 space-y-2">
            {items.map((item) => (
              <button
                key={item.event_id}
                data-testid="activity-item"
                onClick={goCalendar}
                className={`${cardCls} flex w-full items-center gap-3 p-3.5 text-left transition-colors hover:bg-guinep-soft/50`}
              >
                <CalendarCheck size={15} className="shrink-0 text-guinep-mid" />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-guinep-ink">{ACTIVITY_LABELS[item.type] || item.type}</p>
                  {item.detail && <p className="truncate text-xs text-guinep-mute">{item.detail}</p>}
                </div>
                <span className="shrink-0 text-[11px] text-guinep-mute/60">{timeAgo(item.created_at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
